import Tippy from '@tippyjs/react'
import { selectIsInternalAdmin } from 'accessors'
import CopyIcon from 'components/ReusableComponents/Icons/CopyIcon'
import ExclamationIcon from 'components/ReusableComponents/Icons/Exclamation'
import useCopyText from 'hooks/useCopyText'
import { PMSnames } from 'main/hotelBrandsAndChains'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'

import { getHotelImageBaseUrl } from '../../../utils/cdnHelpers'
import { checkIfImageExists } from '../../../utils/common'
import { formatPhoneNumber } from '../../../utils/helpers'
import styles from './HotelCard.module.scss'
import LocaleCard from './LocaleCard/index'

function formatTime(timestring) {
  return new Date('1970-01-01T' + timestring + 'Z').toLocaleTimeString(
    {},
    { timeZone: 'UTC', hour12: true, hour: 'numeric', minute: 'numeric' }
  )
}

const TelecomButton = ({
  id,
  phoneNumbers,
  reservationHotLine,
  pendingRequests,
  invoiceAddress,
  billingAddress,
  streetAddress,
  subscriptions,
  tcrCampaignId,
  status
}) => {
  const [isDisabled, setIsDisabled] = useState(false)
  const [disabledReason, setDisabledReason] = useState(null)

  useEffect(() => {
    const matchingPhoneWithHotline = phoneNumbers.find(
      p => p.phoneNumbers === reservationHotLine
    )

    if (!tcrCampaignId) {
      setIsDisabled(true)
      setDisabledReason('Missing TCR campaign ID')
      return
    }

    if (matchingPhoneWithHotline && matchingPhoneWithHotline.status === 'Provisioning') {
      setIsDisabled(true)
      setDisabledReason('Provisioning')
      return
    }

    if (pendingRequests.find(r => r.requestKind === 'TelecomChangeProvider')) {
      setIsDisabled(true)
      setDisabledReason('Pending telecom request')
      return
    }

    if (!reservationHotLine) {
      setIsDisabled(true)
      setDisabledReason('Missing hotline')
      return
    }

    if (!invoiceAddress) {
      setIsDisabled(true)
      setDisabledReason('Missing invoice address')
      return
    }

    if (!billingAddress) {
      setIsDisabled(true)
      setDisabledReason('Missing billing address')
      return
    }

    if (status === 'PendingCampaignBrandRegistration') {
      setIsDisabled(true)
      setDisabledReason('Pending campaign brand registration')
      return
    }

    if (!streetAddress) {
      setIsDisabled(true)
      setDisabledReason('Missing street in address')
      return
    }

    setIsDisabled(false)
    setDisabledReason(null)
  }, [
    phoneNumbers,
    pendingRequests,
    invoiceAddress,
    billingAddress,
    streetAddress,
    subscriptions,
    reservationHotLine,
    tcrCampaignId,
    status
  ])

  if (phoneNumbers.filter(n => n.capability !== 'TextOnly').length === 0) {
    return (
      <Link
        style={{ pointerEvents: isDisabled ? 'none' : 'initial' }}
        to={`/addTelecom/${id}`}
      >
        <button disabled={isDisabled} className={styles.fixCta}>
          Set up telecom
        </button>
        {isDisabled && (
          <p className={styles.infoText}>
            <ExclamationIcon />

            {disabledReason}
          </p>
        )}
      </Link>
    )
  }

  return null
}

const HotelCard = ({
  id,
  externalHotelId,
  addedOn,
  reservationHotLine,
  pms,
  bookingEngine,
  chainCode,
  primaryWebsiteUrl,
  privacyUrl,
  termsUrl,
  reviewTripAdvisorPage,
  utcOffSet,
  ianaTimeZoneId,
  locale,
  cultureCode,
  currency,
  currencyIsoCode,
  checkInTime,
  checkOutTime,
  billingAddress,
  invoiceAddress,
  streetAddress,
  textMarketingUrlKey,
  hotelName,
  lastAutomatedInfoSyncOn,
  pendingRequests,
  phoneNumbers,
  reservationProxyLine,
  groupName,
  numberOfRooms,
  tzTimeZoneName,
  parentChainCode,
  subscriptions,
  tcrCampaignId,
  status
}) => {
  const gotAllData =
    reservationHotLine &&
    reviewTripAdvisorPage &&
    cultureCode &&
    billingAddress &&
    invoiceAddress

  const [imgUrl, setImgUrl] = useState()
  const isInternalAdmin = useSelector(selectIsInternalAdmin)

  useEffect(() => {
    async function checkImg() {
      const potentialImgUrl = getHotelImageBaseUrl() + textMarketingUrlKey + '.jpg'

      checkIfImageExists(potentialImgUrl)
        .then(() => {
          setImgUrl(potentialImgUrl)
        })
        .catch(() => {
          setImgUrl(null)
        })
    }

    checkImg()
  }, [textMarketingUrlKey])

  const copy = useCopyText()

  return (
    <div className={styles.hotelCard}>
      <div className={styles.hotelPhotoHolder} onDoubleClick={() => copy(id)}>
        <figure className={styles.hotelPhoto}>
          {imgUrl && <img src={imgUrl} alt={hotelName} />}
        </figure>
      </div>
      <div className={styles.hotelInfo}>
        <div className={styles.beInfo}>
          <div className={styles.infoCol1}>
            <Info label="Group" value={groupName} />
            <Info label="Rooms" value={numberOfRooms} />
            <Info
              label="Check in/out"
              value={
                checkInTime && checkOutTime
                  ? `${formatTime(checkInTime)} / ${formatTime(checkOutTime)}`
                  : undefined
              }
            />
            <Info label="Hotel ID" value={externalHotelId} />
            <Info
              label="Added on"
              value={moment(addedOn).format('MMM DD, HH:MM')}
              tooltipHint={moment(addedOn).format('MMM DD, YYYY h:MM A')}
            />
          </div>
          <div className={styles.infoCol2}>
            <Info label="BE" value={bookingEngine} />
            <Info label="PMS" value={PMSnames[pms]} />
            <Info label="Parent chain" value={parentChainCode} />
            <Info label="Chain" value={chainCode} />
            <Info
              label="Last BE sync"
              value={
                lastAutomatedInfoSyncOn
                  ? moment(lastAutomatedInfoSyncOn).format('MMM DD, HH:MM')
                  : 'Not synced'
              }
              tooltipHint={
                lastAutomatedInfoSyncOn
                  ? moment(lastAutomatedInfoSyncOn).format('MMM DD, YYYY h:MM A')
                  : null
              }
            />
          </div>
        </div>
        <BottomBar>
          <HLink name="Site" url={primaryWebsiteUrl} />
          <HLink name="TripAdvisor" url={reviewTripAdvisorPage} />
          <HLink name="Privacy" url={privacyUrl} />
          <HLink name="Terms" url={termsUrl} />
          <Phone name="Hotline" number={reservationHotLine} />
          {isInternalAdmin && (
            <>
              <Phone name="Proxy" number={reservationProxyLine} />
              <LocaleCard
                utcOffSet={utcOffSet}
                ianaTimeZoneId={ianaTimeZoneId}
                locale={locale || cultureCode}
                currency={currency || currencyIsoCode}
                tzTimeZoneName={tzTimeZoneName}
              />
            </>
          )}
        </BottomBar>
        {isInternalAdmin && (
          <div className={styles.editButtons}>
            <Link to={`/editHotel/${id}/`}>
              {gotAllData ? (
                <button className={styles.cta}>Edit hotel</button>
              ) : (
                <button className={styles.fixCta}>Add missing info</button>
              )}
            </Link>
            <TelecomButton
              id={id}
              phoneNumbers={phoneNumbers}
              reservationHotLine={reservationHotLine}
              pendingRequests={pendingRequests}
              invoiceAddress={invoiceAddress}
              billingAddress={billingAddress}
              streetAddress={streetAddress}
              subscriptions={subscriptions}
              tcrCampaignId={tcrCampaignId}
              status={status}
            />
          </div>
        )}
      </div>
    </div>
  )
}

const HLink = ({ url, name }) => (
  <div className={styles.link}>
    {url ? (
      <a target="_blank" rel="noopener noreferrer" href={url}>
        {name}
      </a>
    ) : (
      <span className={styles.missing}>
        <small>No</small> {name}
      </span>
    )}
  </div>
)

const Phone = ({ number, name }) => (
  <div className={styles.phoneLink}>
    {number ? (
      <div className={styles.phoneLinkInner}>
        <span className={styles.phoneLabel}>{name}</span>
        <span className={styles.phoneValue}>{formatPhoneNumber(number)}</span>
      </div>
    ) : (
      <span className={styles.missing}>No {name}</span>
    )}
  </div>
)

const BottomBar = ({ children }) => <div className={styles.bottomBar}>{children}</div>

const Info = ({ label, value, tooltipHint }) => {
  const copy = useCopyText()

  return (
    <div className={styles.inlineInfo}>
      <div className={styles.inlineInfoLabel}>{label}</div>
      <Tippy disabled={!tooltipHint} content={tooltipHint}>
        <div className={styles.inlineInfoValue} title={value}>
          {value ? value : <span className={styles.missing}>Missing data</span>}
        </div>
      </Tippy>

      {value && (
        <button className={styles.inlineCopyButton} onClick={() => copy(value)}>
          <CopyIcon />
        </button>
      )}
    </div>
  )
}

export default HotelCard
